import { SortDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Error } from "@progress/kendo-react-labels";
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from "@progress/kendo-react-layout";
import cloneDeep from "lodash-es/cloneDeep";
import { SearchIcon, XCircle as CloseIcon } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Else, If, Then } from "react-if";
import { CollapseContainer } from "@/components/Card/Collapse";
import FormLabel from "@/components/Kendo/Form/FormLabel";
import { Search } from "@/components/Search";
import { useDebounce } from "@/hooks/useDebounce";
import { QueryMethod } from "@/hooks/usePaginateQuery";
import useQuery from "@/hooks/useQuery";
import "./index.scss";

/**
 * Supported API response shapes: object with items (and optional total) or a plain array.
 * Use a plain array for non-paginated APIs that return e.g. string[]; use parseResponse to map to DataItem[].
 */
export type MultiSelectTabsResponseShape =
  | { items: unknown[]; total?: number }
  | unknown[];

/** Response must be { items; total? } or a plain array (non-paginated). Use parseResponse when API returns a raw array. */
export interface MultiSelectTabsTabConfig<
  Args extends Record<string, unknown>,
  Response extends MultiSelectTabsResponseShape,
  DataItem extends Record<string, unknown>,
> {
  key: string;
  title: React.ReactNode;
  method?: QueryMethod<Args, Response>;
  params?: Args;
  sort?: SortDescriptor;
  perPage?: number;
  /** When false, API is called without pageNo/pageSize/sortOrder/sortBy; response is treated as a single page (no scroll pagination). Default true. */
  paginated?: boolean;
  data?: DataItem[];
  /** Map raw response items to DataItem[]. Required when API returns a plain array (e.g. string[]). */
  parseResponse?: (items: unknown[]) => DataItem[];
  /** Optional per-tab prefixes for custom item creation. When set (and allowCustom is true), one add-custom button is rendered per prefix using "<prefix>.<searchValue>". */
  customItemPrefixes?: string[];
}

export interface IMultiSelectTabsProps<
  Args extends Record<string, unknown>,
  Response extends MultiSelectTabsResponseShape,
  DataItem extends Record<string, unknown>,
> extends Partial<FieldRenderProps> {
  tabs: MultiSelectTabsTabConfig<Args, Response, DataItem>[];
  rightPanelTitle?: string;
  emptySearchMessage?: string;
  emptyAssignedMessage?: string;
  emptyAssignedDescription?: string;
  allowCustom?: boolean;
  /** When allowCustom is true, validates the custom value before adding. Return true if valid, or an error message string. Default: allow only letters, numbers, underscore and hyphen. */
  customItemValidator?: (value: string) => true | string;
  textField?: string;
  dataItemKey?: string;
  perPage?: number;
  /** When true, shows loading state in the list area (e.g. initial load). Combines with internal query loading when not set. */
  isLoading?: boolean;
  rightItemRender?: (item: DataItem) => React.ReactNode;
  leftItemRender?: (item: DataItem) => React.ReactNode;
  showSelectAll?: boolean;
  clientSideSearch?: boolean;
}

type TabListState<DataItem extends Record<string, unknown>> = {
  pageNo: number;
  accumulatedItems: DataItem[];
  hasMore: boolean;
};

const SCROLL_THRESHOLD = 100;
const DEFAULT_PER_PAGE = 20;

/** Default allowed format: letters, numbers, underscore, hyphen and dot (e.g. "aB_-zz", "db1.Role_555"). */
const DEFAULT_CUSTOM_ITEM_REGEX = /^[a-zA-Z0-9_.-]+$/;

function MultiSelectTabs<
  Args extends Record<string, unknown>,
  Response extends MultiSelectTabsResponseShape,
  DataItem extends Record<string, unknown>,
>(props: IMultiSelectTabsProps<Args, Response, DataItem>) {
  const {
    tabs,
    value: formValue = {},
    onChange,
    name,
    id = name,
    label,
    valid,
    disabled,
    validationMessage,
    touched,
    rightPanelTitle,
    emptySearchMessage,
    emptyAssignedMessage,
    emptyAssignedDescription,
    allowCustom = false,
    customItemValidator,
    textField = "name",
    dataItemKey = "id",
    perPage = DEFAULT_PER_PAGE,
    isLoading: isLoadingProp,
    FieldWrapperClassName,
    rightItemRender,
    leftItemRender,
    showSelectAll = false,
    ..._rest
  } = props;

  const { t } = useTranslation();
  const defaultCustomItemValidator = useCallback(
    (value: string): true | string =>
      DEFAULT_CUSTOM_ITEM_REGEX.test(value)
        ? true
        : t("multiSelectTabs.customItemInvalidFormat"),
    [t],
  );
  const effectiveCustomItemValidator =
    customItemValidator ?? defaultCustomItemValidator;
  const showValidationMessage = touched && validationMessage;
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = label ? `${id}_label` : "";

  const value = useMemo(
    () => (formValue ?? {}) as Record<string, DataItem[]>,
    [formValue],
  );
  const setValue = useCallback(
    (next: Record<string, DataItem[]>) => {
      onChange?.({ value: next });
    },
    [onChange],
  );

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [customItemError, setCustomItemError] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 400);

  const activeTab = tabs[activeTabIndex];
  const activeKey = activeTab?.key ?? "";

  const [tabListStates, setTabListStates] = useState<
    Record<string, TabListState<DataItem>>
  >({});
  const isFetchingRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeKeyRef = useRef(activeKey);
  activeKeyRef.current = activeKey;
  /** Tab that the current `data` belongs to; set when we have data so the effect always writes to the correct tab. */
  const dataOwnerKeyRef = useRef<string>(activeKey);
  /** Prevents the search-reset effect from wiping tab state on initial mount. */
  const searchResetMountedRef = useRef(false);

  const defaultSort: SortDescriptor = useMemo(
    () => ({ field: "name", dir: "asc" as const }),
    [],
  );

  const tabState = tabListStates[activeKey] ?? {
    pageNo: 1,
    accumulatedItems: [],
    hasMore: true,
  };
  const { pageNo, accumulatedItems, hasMore } = tabState;

  const sort = activeTab?.sort ?? defaultSort;
  const sortOrder = (sort.dir?.toUpperCase() ?? "ASC") as "ASC" | "DESC";
  const sortBy = sort.field ?? "name";
  const tabPerPage = activeTab?.perPage ?? perPage;
  const isPaginated = activeTab?.paginated !== false;
  /** Tracks current isPaginated so the search-reset effect can read it without adding it to deps. */
  const isPaginatedRef = useRef(isPaginated);
  isPaginatedRef.current = isPaginated;

  const noopMethod = useCallback(
    (_args: Args, _opts?: { signal?: AbortSignal }) =>
      Promise.resolve({ items: [], total: 0 } as unknown as Response),
    [],
  );

  const queryParams = useMemo(() => {
    const base = {
      ...(activeTab?.params ?? {}),
    } as unknown as Args;
    if (isPaginated) {
      // Paginated tabs: send search + pagination to the API.
      // Non-paginated tabs: search is applied client-side in listItems.
      Object.assign(base, {
        search: debouncedSearch || undefined,
        pageNo,
        pageSize: tabPerPage,
        sortOrder,
        sortBy,
      });
    }
    return base;
  }, [
    activeTab?.params,
    debouncedSearch,
    isPaginated,
    pageNo,
    tabPerPage,
    sortOrder,
    sortBy,
  ]);

  const queryKeyForTab = useMemo(
    () => (activeKey ? [activeKey, queryParams] : []),
    [activeKey, queryParams],
  );
  const { data, isLoading } = useQuery<Args, Response>({
    method: (activeTab?.method ?? noopMethod) as QueryMethod<Args, Response>,
    params: queryParams,
    queryKey: queryKeyForTab,
    options: {
      enabled:
        !!activeTab?.method &&
        activeKey !== "" &&
        !(activeTab as MultiSelectTabsTabConfig<Args, Response, DataItem>).data,
    },
  });

  if (data != null && activeKey) dataOwnerKeyRef.current = activeKey;

  /** Combined loading: explicit prop true or internal query loading when prop not set. */
  const isLoadingState =
    isLoadingProp === true || (isLoadingProp !== false && isLoading);

  // Apply fetched data only to the tab it belongs to (use ref set when we have data).
  useEffect(() => {
    if (!activeTab?.method || !activeKey) return;
    if (isLoading && pageNo > 1) return;
    if (!data) return;

    const tabKey = dataOwnerKeyRef.current;
    if (!tabKey) return;
    const rawItems: unknown[] = Array.isArray(data)
      ? data
      : "items" in data
        ? (data as { items: unknown[] }).items
        : [];
    const total = Array.isArray(data)
      ? undefined
      : (data as { total?: number }).total;
    const items = activeTab.parseResponse
      ? activeTab.parseResponse(cloneDeep(rawItems))
      : (cloneDeep(rawItems) as DataItem[]);

    setTabListStates(prev => {
      const current = prev[tabKey] ?? {
        pageNo: 1,
        accumulatedItems: [],
        hasMore: true,
      };
      const newItems =
        current.pageNo === 1 ? items : [...current.accumulatedItems, ...items];
      const hasMorePages =
        isPaginated && typeof total === "number" && newItems.length < total;
      return {
        ...prev,
        [tabKey]: {
          ...current,
          accumulatedItems: newItems,
          hasMore: hasMorePages,
        },
      };
    });
    isFetchingRef.current = false;
  }, [data, isLoading, activeKey, activeTab, pageNo, isPaginated]);

  // Reset list only when search changes (so a new search fetches from page 1).
  // Do NOT reset when switching tabs — preserve each tab’s accumulated items.
  // Skip the initial mount run: React always fires effects on mount regardless of
  // whether the dep "changed", which would wipe cached query data before the data
  // effect has a chance to persist it.
  // Non-paginated tabs never need a reset here — their search is handled client-side
  // inside listItems without mutating accumulatedItems.
  useEffect(() => {
    if (!searchResetMountedRef.current) {
      searchResetMountedRef.current = true;
      return;
    }
    if (!isPaginatedRef.current) return;
    const key = activeKeyRef.current;
    if (!key) return;
    setTabListStates(prev => ({
      ...prev,
      [key]: {
        pageNo: 1,
        accumulatedItems: [],
        hasMore: true,
      },
    }));
    isFetchingRef.current = false;
  }, [debouncedSearch]);

  useEffect(() => {
    setCustomItemError(null);
  }, [search]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !activeTab?.method || !isPaginated || !hasMore) return;

    const onScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const { scrollTop, scrollHeight, clientHeight } = target;
      if (
        scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD &&
        !isFetchingRef.current &&
        hasMore
      ) {
        isFetchingRef.current = true;
        setTabListStates(prev => {
          const cur = prev[activeKey] ?? {
            pageNo: 1,
            accumulatedItems: [],
            hasMore: true,
          };
          return {
            ...prev,
            [activeKey]: { ...cur, pageNo: cur.pageNo + 1 },
          };
        });
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeKey, hasMore, activeTab?.method, isPaginated]);

  const listItems: DataItem[] = useMemo(() => {
    const term = (debouncedSearch || "").toLowerCase();
    const filterByTerm = (items: DataItem[]) => {
      if (!term) return items;
      const textKey = textField as keyof DataItem;
      return items.filter(item =>
        String(item[textKey] ?? "")
          .toLowerCase()
          .includes(term),
      );
    };

    if (activeTab?.data) return filterByTerm(activeTab.data);

    // Non-paginated API tabs filter client-side; paginated tabs rely on server-side search.
    if (!isPaginated) return filterByTerm(accumulatedItems);

    return accumulatedItems;
  }, [
    activeTab?.data,
    debouncedSearch,
    accumulatedItems,
    textField,
    isPaginated,
  ]);

  const selectedForTab = useCallback(
    (tabKey: string) => value[tabKey] ?? [],
    [value],
  );

  const isItemSelected = useCallback(
    (tabKey: string, item: DataItem) => {
      const selected = selectedForTab(tabKey);
      const keyVal = item[dataItemKey as keyof DataItem];
      return selected.some(
        s => (s[dataItemKey as keyof DataItem] as unknown) === keyVal,
      );
    },
    [selectedForTab, dataItemKey],
  );

  const toggleItem = useCallback(
    (tabKey: string, item: DataItem) => {
      const selected = selectedForTab(tabKey);
      const keyVal = item[dataItemKey as keyof DataItem] as unknown;
      const isSelected = selected.some(
        s => (s[dataItemKey as keyof DataItem] as unknown) === keyVal,
      );
      let next: DataItem[];
      if (isSelected) {
        next = selected.filter(
          s => (s[dataItemKey as keyof DataItem] as unknown) !== keyVal,
        );
      } else {
        next = [...selected, item];
      }
      setValue({ ...value, [tabKey]: next });
    },
    [value, setValue, selectedForTab, dataItemKey],
  );

  const removeItem = useCallback(
    (tabKey: string, item: DataItem) => {
      const selected = selectedForTab(tabKey);
      const keyVal = item[dataItemKey as keyof DataItem] as unknown;
      const next = selected.filter(
        s => (s[dataItemKey as keyof DataItem] as unknown) !== keyVal,
      );
      setValue({ ...value, [tabKey]: next });
    },
    [value, setValue, selectedForTab, dataItemKey],
  );

  const selectAllChecked =
    listItems.length > 0 &&
    listItems.every(item => isItemSelected(activeKey, item));
  // const selectAllIndeterminate =
  //   listItems.some(item => isItemSelected(activeKey, item)) &&
  //   !selectAllChecked;

  const onSelectAllChange = useCallback(() => {
    const selected = selectedForTab(activeKey);
    const keyField = dataItemKey as keyof DataItem;
    if (selectAllChecked) {
      const next = selected.filter(
        s =>
          !listItems.some(
            li => (li[keyField] as unknown) === (s[keyField] as unknown),
          ),
      );
      setValue({ ...value, [activeKey]: next });
    } else {
      const existingIds = new Set(
        selected.map(s => s[keyField] as unknown as string | number),
      );
      const toAdd = listItems.filter(
        li => !existingIds.has(li[keyField] as unknown as string | number),
      );
      setValue({ ...value, [activeKey]: [...selected, ...toAdd] });
    }
  }, [
    activeKey,
    selectAllChecked,
    listItems,
    selectedForTab,
    value,
    setValue,
    dataItemKey,
  ]);

  const addCustomItemForTerm = useCallback(
    (raw: string, options?: { keepSearchOpen?: boolean }) => {
      const term = raw.trim();
      if (!term || !allowCustom) return;
      const validationResult = effectiveCustomItemValidator(term);
      if (validationResult !== true) {
        setCustomItemError(validationResult);
        return;
      }
      setCustomItemError(null);
      const newItem = {
        [dataItemKey]: term,
        [textField]: term,
      } as DataItem;
      const selected = selectedForTab(activeKey);
      const keyVal = newItem[dataItemKey as keyof DataItem] as unknown;
      const exists = selected.some(
        s => (s[dataItemKey as keyof DataItem] as unknown) === keyVal,
      );
      if (exists) return;
      setValue({ ...value, [activeKey]: [...selected, newItem] });
      // When using prefix buttons, keep search so the user can add remaining prefixes without the container resetting.
      if (!options?.keepSearchOpen) {
        setSearch("");
      }
    },
    [
      allowCustom,
      effectiveCustomItemValidator,
      activeKey,
      selectedForTab,
      value,
      setValue,
      dataItemKey,
      textField,
    ],
  );

  const addCustomItem = useCallback(() => {
    const term = search.trim();
    if (!term) return;
    addCustomItemForTerm(term);
  }, [search, addCustomItemForTerm]);

  const defaultRightTitle = t("multiSelectTabs.rightPanelTitle");
  const defaultEmptySearch = t("multiSelectTabs.emptySearchMessage");
  const defaultEmptyAssigned = t("multiSelectTabs.emptyAssignedMessage");
  const defaultEmptyDesc = t("multiSelectTabs.emptyAssignedDescription", {
    tabTitles: tabs
      .map(tab => (typeof tab.title === "string" ? tab.title : tab.key))
      .join(", "),
    rightTitle: rightPanelTitle ?? defaultRightTitle,
  });

  const showEmptyList =
    listItems.length === 0 && !(isLoadingState && pageNo === 1);
  const totalAssigned = useMemo(
    () => Object.values(value).reduce((sum, arr) => sum + arr.length, 0),
    [value],
  );

  return (
    <FieldWrapper className={FieldWrapperClassName}>
      <FormLabel
        id={labelId}
        editorId={id}
        editorValid={!!valid}
        editorDisabled={disabled}
        label={""}
        value={totalAssigned > 0 ? String(totalAssigned) : undefined}
      >
        <div
          className="tlx-multi-select-tabs"
          data-test-id="multi-select-tabs"
          aria-describedby={errorId}
          aria-labelledby={labelId}
        >
          <div className="tlx-multi-select-tabs__left">
            <TabStrip
              selected={activeTabIndex}
              onSelect={(e: TabStripSelectEventArguments) =>
                setActiveTabIndex(e.selected)
              }
              className="tlx-dialog-tab-strip with-border"
            >
              {tabs.map(tab => (
                <TabStripTab key={tab.key} title={tab.title} />
              ))}
            </TabStrip>

            <div className="tlx-multi-select-tabs__search-wrap">
              <Search
                placeholder={t("multiSelectTabs.search")}
                onChange={setSearch}
                value={search}
                className="tlx-multi-select-tabs__search-input"
                data-test-id="multi-select-tabs-search"
                disabled={disabled}
              />
            </div>

            <div
              ref={scrollContainerRef}
              className="tlx-multi-select-tabs__list"
              data-test-id="multi-select-tabs-list"
              role="listbox"
            >
              <If condition={showEmptyList}>
                <Then>
                  <div className="tlx-multi-select-tabs__empty-search">
                    <SearchIcon height={20} width={20} />
                    <p className="tlx-multi-select-tabs__empty-search-title">
                      {emptySearchMessage ?? defaultEmptySearch}
                    </p>
                    <p className="tlx-multi-select-tabs__empty-search-desc">
                      {t("multiSelectTabs.emptySearchDescription")}
                    </p>
                    <If condition={allowCustom && search.trim().length > 0}>
                      <Then>
                        <div>
                          {activeTab?.customItemPrefixes &&
                          activeTab.customItemPrefixes.length > 0 ? (
                            <div className="tlx-multi-select-tabs__custom-prefix-buttons">
                              {activeTab.customItemPrefixes.map(prefix => {
                                const trimmed = search.trim();
                                const combined = `${prefix}.${trimmed}`;
                                const selected = selectedForTab(activeKey);
                                const alreadyAdded = selected.some(
                                  s =>
                                    (s[
                                      dataItemKey as keyof DataItem
                                    ] as unknown) === combined,
                                );
                                return (
                                  <Button
                                    size="medium"
                                    type="button"
                                    icon="plus"
                                    key={prefix}
                                    onClick={() =>
                                      addCustomItemForTerm(combined, {
                                        keepSearchOpen: true,
                                      })
                                    }
                                    disabled={alreadyAdded}
                                    data-test-id={`multi-select-tabs-add-custom-${prefix}`}
                                    aria-describedby={
                                      customItemError
                                        ? "multi-select-tabs-custom-item-error"
                                        : undefined
                                    }
                                  >
                                    {combined}
                                  </Button>
                                );
                              })}
                            </div>
                          ) : (
                            <Button
                              size="medium"
                              type="button"
                              onClick={addCustomItem}
                              data-test-id="multi-select-tabs-add-custom"
                              aria-describedby={
                                customItemError
                                  ? "multi-select-tabs-custom-item-error"
                                  : undefined
                              }
                            >
                              {t("multiSelectTabs.addCustom")} &quot;
                              {search.trim()}&quot;
                            </Button>
                          )}
                          {customItemError && (
                            <div
                              id="multi-select-tabs-custom-item-error"
                              data-test-id="multi-select-tabs-custom-item-error"
                            >
                              <Error>{customItemError}</Error>
                            </div>
                          )}
                        </div>
                      </Then>
                    </If>
                  </div>
                </Then>
                <Else>
                  <If
                    condition={
                      isLoadingState && pageNo === 1 && listItems.length === 0
                    }
                  >
                    <Then>
                      <div
                        className="tlx-multi-select-tabs__loading-initial"
                        data-test-id="multi-select-tabs-loading"
                        role="status"
                        aria-live="polite"
                      >
                        <span className="k-icon k-font-icon k-i-loading" />
                        <span>{t("multiSelectTabs.loading")}</span>
                      </div>
                    </Then>
                    <Else>
                      <If
                        condition={
                          !activeTab?.data &&
                          listItems.length > 0 &&
                          showSelectAll
                        }
                      >
                        <Then>
                          <div className="tlx-multi-select-tabs__select-all">
                            <Checkbox
                              checked={selectAllChecked}
                              // indeterminate={selectAllIndeterminate}
                              onChange={onSelectAllChange}
                              disabled={disabled}
                              data-test-id="multi-select-tabs-select-all"
                            />
                            <span>{t("multiSelectTabs.selectAll")}</span>
                          </div>
                        </Then>
                      </If>
                      {listItems.map((item, idx) => {
                        const textVal = item[textField as keyof DataItem];
                        const keyVal = item[dataItemKey as keyof DataItem];
                        const checked = isItemSelected(activeKey, item);
                        return (
                          <div
                            key={`${keyVal}-${idx}`}
                            className="tlx-multi-select-tabs__list-item"
                            role="option"
                            aria-selected={checked}
                            onClick={() =>
                              !disabled && toggleItem(activeKey, item)
                            }
                          >
                            <Checkbox
                              checked={checked}
                              onChange={() => {}}
                              disabled={disabled}
                              data-test-id={`multi-select-tabs-item-${String(idx)}`}
                            />
                            <div
                              className="tlx-multi-select-tabs__list-item-label"
                              title={String(textVal ?? keyVal)}
                            >
                              {leftItemRender
                                ? leftItemRender(item)
                                : String(textVal ?? keyVal)}
                            </div>
                          </div>
                        );
                      })}
                      <If
                        condition={
                          activeTab?.method && isLoadingState && pageNo > 1
                        }
                      >
                        <Then>
                          <div className="tlx-multi-select-tabs__loading-more">
                            {t("multiSelectTabs.loading")}
                          </div>
                        </Then>
                      </If>
                    </Else>
                  </If>
                </Else>
              </If>
            </div>
          </div>

          <div className="tlx-multi-select-tabs__right">
            <h3 className="tlx-multi-select-tabs__right-title">
              {rightPanelTitle ?? defaultRightTitle}
            </h3>

            <If condition={totalAssigned === 0}>
              <Then>
                <div className="tlx-multi-select-tabs__empty-assigned">
                  <span
                    className="k-icon k-font-icon k-i-file-txt"
                    style={{ fontSize: "3em" }}
                    aria-hidden
                  />
                  <p className="tlx-multi-select-tabs__empty-assigned-primary">
                    {emptyAssignedMessage ?? defaultEmptyAssigned}
                  </p>
                  <p className="tlx-multi-select-tabs__empty-assigned-secondary">
                    {emptyAssignedDescription ?? defaultEmptyDesc}
                  </p>
                </div>
              </Then>
              <Else>
                <div className="tlx-multi-select-tabs__sections">
                  {tabs.map(tab => {
                    const items = value[tab.key] ?? [];
                    if (items.length === 0) return null;
                    return (
                      <CollapseContainer
                        key={tab.key}
                        expand
                        title={
                          <span
                            data-test-id={`multi-select-tabs-section-${tab.key}`}
                          >
                            {tab.title} ({items.length})
                          </span>
                        }
                        className="tlx-multi-select-tabs__section"
                      >
                        <div className="tlx-multi-select-tabs__chips">
                          {items.map((item, _idx) => {
                            const textVal = item[textField as keyof DataItem];
                            const keyVal = item[dataItemKey as keyof DataItem];
                            return (
                              <div
                                key={(keyVal as string) ?? _idx}
                                className="k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base tlx-multi-select-tabs__chip"
                                role="listitem"
                              >
                                <span className="k-chip-content">
                                  <span
                                    className="k-chip-label tlx-multi-select-tabs__chip-label"
                                    title={String(textVal ?? keyVal)}
                                  >
                                    {rightItemRender
                                      ? rightItemRender(item)
                                      : String(textVal ?? keyVal)}
                                  </span>
                                </span>
                                <span className="k-chip-actions">
                                  <span className="k-chip-action">
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => removeItem(tab.key, item)}
                                      onKeyDown={e => {
                                        if (
                                          e.key === "Enter" ||
                                          e.key === " "
                                        ) {
                                          e.preventDefault();
                                          removeItem(tab.key, item);
                                        }
                                      }}
                                      aria-label={t("multiSelectTabs.remove")}
                                      data-test-id={`multi-select-tabs-remove-${tab.key}-${String(keyVal)}`}
                                    >
                                      <CloseIcon />
                                    </span>
                                  </span>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </CollapseContainer>
                    );
                  })}
                </div>
              </Else>
            </If>
          </div>
        </div>

        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </FormLabel>
    </FieldWrapper>
  );
}

export default MultiSelectTabs;
